import { SyntheticEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, IconButton, Input } from '@treezor/design-system';

import { useTag } from '@/features/user/hooks/useTag';

import { useAccesControl } from '@/hooks/useAccessControl';
import { useConfirmationDialog } from '@/hooks/useConfirmationModal';

import { featuresSlugs } from '@/features/roles/store/models/features.model';

export function UserTag() {
  const { t } = useTranslation();
  const { hasAccessToFeature } = useAccesControl();

  const {
    isDisabled,
    isOnEditMode,
    register,
    toggleEditMode,
    onCancel,
    handleSubmit,
    setTag,
    isLoading,
    deleteTag,
    userTagWatch,
    user,
  } = useTag();

  const DeleteTagConfirmationModal = useConfirmationDialog({
    id: 'delete-restrictions-group-confirmation-modal',
    title: t('pages.currentProfiles.profile.tag.deleteModal.title'),
    description: t('pages.currentProfiles.profile.tag.deleteModal.confirmationMessage'),
    confirm: {
      color: 'danger',
      label: t('pages.cores.delete'),
      action: () => {
        deleteTag();
        toggleDeleteTagConfirmationModal();
      },
    },
  });

  const toggleDeleteTagConfirmationModal = useCallback(
    () => DeleteTagConfirmationModal.toggle(),
    [DeleteTagConfirmationModal],
  );

  return (
    <>
      <div className="flex flex-col gap-5">
        <h6 className="typo-h6-bold text-secondary">{t('pages.currentProfiles.profile.informations.tag')}</h6>
        <div className="flex gap-5 px-6 py-5 rounded-200 border-primary border-2 items-center">
          {isOnEditMode ? (
            <div className="flex-grow">
              <Input
                type="text"
                label=""
                {...register}
                onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  setTag(event.currentTarget.value)
                }
                defaultValue={userTagWatch}
                size="s"
                className="w-full"
                testId="user-tag"
              />
            </div>
          ) : (
            <p className="typo-body-regular-paragraph text-secondary flex-grow">{user.userTag || '/'}</p>
          )}

          {hasAccessToFeature(featuresSlugs.EDIT_INFORMATIONS) && !isDisabled && (
            <div className="flex-shrink-0">
              {isOnEditMode ? (
                <div className="flex gap-4">
                  <Button variant="ghost" size="s" onClick={onCancel} disabled={!!isLoading} testId="tag-cancel-button">
                    {t('pages.cores.cancel')}
                  </Button>
                  <Button size="s" onClick={handleSubmit} isLoading={!!isLoading} testId="tag-save-button">
                    {t('pages.cores.save')}
                  </Button>
                </div>
              ) : (
                <>
                  <IconButton variant="secondary" size="s" icon="edit" onClick={toggleEditMode} />
                  {user.userTag && (
                    <IconButton
                      variant="secondary"
                      size="s"
                      icon="delete"
                      onClick={toggleDeleteTagConfirmationModal}
                      isLoading={isLoading}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <DeleteTagConfirmationModal.ConfirmationDialog />
    </>
  );
}
